import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import { PropTypes } from 'prop-types';
import { ConversationsClientContext } from './ConversationsClientProvider';

export const ConversationsContext = createContext();

const sortByNewestMessage = conversations => {
    const sorted = [...conversations];
    return sorted.sort((a, b) => {
        const aDate = a.channelState.lastMessage?.dateCreated;
        const bDate = b.channelState.lastMessage?.dateCreated;
        return aDate > bDate ? -1 : aDate < bDate ? 1 : 0;
    });
};

const ConversationsProvider = ({ children }) => {
    const [isReady, setIsReady] = useState(false);
    const [isFirstLoad, setIsFirstLoad] = useState(true);
    const initialConversationCount = useRef();
    const conversationsLoadedCount = useRef(0);
    const [conversations, setConversations] = useState();
    const [hasMoreConversations, setHasMoreConversations] = useState();
    const [currentConversation, setCurrentConversation] = useState();
    const { conversationsClient } = useContext(ConversationsClientContext);
    const isMyIcscInbox = window.location.pathname.startsWith('/my-icsc/messages/inbox');

    const handleConversationUpdated = async update => {
        console.log('%c conversation updated! ', 'background: #222; color: #bada55');
        console.log({ update });
        // Get all conversations and replace this one. Happens for .updateLastReadMessageIndex()
        const { conversations: convos } = await fetchAllConversations();
        const i = convos.findIndex(c => c.sid === update.conversation.sid);
        const newConvos = [...convos];
        newConvos[i] = update.conversation;
        const sortedByNewest = sortByNewestMessage(newConvos);
        const totalConversationsCount = sortedByNewest.length;
        initialConversationCount.current = totalConversationsCount;

        if (sortedByNewest.length === 0) {
            setConversations([]);
            setIsReady(true);
        }

        setConversations(sortedByNewest);
    };

    const handleConversationJoin = async newConversation => {
        // This skips loading all conversations on every conversation join in my-icsc to prevent slowdown
        if (isFirstLoad && isMyIcscInbox) {
            return;
        }

        // gave up trying to keep track of conversations like this because state was
        // so just replacing them instead of adding and subtracting
        // setConversations([...conversationRef.current, conversation]);
        console.log('%c conversation joined! ', 'background: #222; color: #bada55');
        let { sortedByNewest } = await fetchFirstPageConversations();

        // newest conversation might not have anything yet for channelState.lastMessage,
        // so remove it and then put it in front
        if (!newConversation.channelState.lastMessage) {
            // console.log('putting this one first', newConversation);
            sortedByNewest = sortedByNewest.filter(c => c.sid !== newConversation.sid);
            sortedByNewest.unshift(newConversation);
        }
        setConversations(sortedByNewest);

        conversationsLoadedCount.current += 1;
        if (conversationsLoadedCount.current === initialConversationCount.current) {
            setIsReady(true);
        }
    };

    const handleConversationLeft = async thisConversation => {
        // setConversations([...conversationRef.current.filter(it => it !== thisConversation)]);
        console.log('%c conversation left! ', 'background: #222; color: #bada55');
        await fetchFirstPageConversations();
    };

    const selectConversation = async conversation => {
        // set last message read as last message
        // console.log('Selecting conversation', conversation, conversation?.channelState.lastMessage?.index);
        if (conversation) {
            const lastMessgeIndex = conversation.channelState.lastMessage?.index || 0;
            console.log(
                `%c selecting conversation & reset unread! ${lastMessgeIndex}`,
                'background: #222; color: #bada55'
            );
            await conversation.updateLastReadMessageIndex(lastMessgeIndex);
        }
        setCurrentConversation(conversation);
    };

    const sendMessageToConversation = async ({ conversation, messageText, messageAttributes }) => {
        console.log('%c sending', 'background: #222; color: #bada55');
        const count = await conversation.sendMessage(messageText, messageAttributes);
        await conversation.updateLastReadMessageIndex(count);
        return count;
    };

    const fetchFirstPageConversations = async () => {
        const subscribedConversations = await conversationsClient.getSubscribedConversations({
            pageSize: 50,
        });

        if (!subscribedConversations) return [];

        const convos = subscribedConversations.items;

        if (subscribedConversations.hasNextPage) {
            setHasMoreConversations(true);
        }

        const sortedByNewest = sortByNewestMessage(convos);
        const totalConversationsCount = sortedByNewest.length;
        initialConversationCount.current = totalConversationsCount;

        if (sortedByNewest.length === 0) {
            setConversations([]);
            setIsReady(true);
        }

        return { conversations: convos, sortedByNewest };
    };

    const fetchAllConversations = async () => {
        let subscribedConversations = await conversationsClient.getSubscribedConversations({
            pageSize: 50,
        });

        if (!subscribedConversations) return [];

        let convos = subscribedConversations.items;

        while (subscribedConversations.hasNextPage) {
            subscribedConversations = await subscribedConversations.nextPage();
            convos = [...convos, ...subscribedConversations.items];
        }

        const sortedByNewest = sortByNewestMessage(convos);
        const totalConversationsCount = sortedByNewest.length;
        initialConversationCount.current = totalConversationsCount;

        if (sortedByNewest.length === 0) {
            setConversations([]);
            setIsReady(true);
        }

        setConversations(sortedByNewest);
        return { conversations: convos, sortedByNewest };
    };

    const fetchPaginatedConversations = async (pageNumber, pageSize) => {
        let subscribedConversations = await conversationsClient.getSubscribedConversations({
            pageSize,
        });

        if (!subscribedConversations) return [];

        let convos = subscribedConversations.items;

        let tempPageNumber = pageNumber;

        while (tempPageNumber > 0 && subscribedConversations.hasNextPage) {
            subscribedConversations = await subscribedConversations.nextPage();
            convos = [...convos, ...subscribedConversations.items];
            tempPageNumber -= 1;
        }

        if (subscribedConversations.hasNextPage) {
            setHasMoreConversations(true);
        } else {
            setHasMoreConversations(false);
        }

        const sortedByNewest = sortByNewestMessage(convos);
        const totalConversationsCount = sortedByNewest.length;
        initialConversationCount.current = totalConversationsCount;

        if (sortedByNewest.length === 0) {
            setConversations([]);
            setIsReady(true);
        }

        setConversations(sortedByNewest);
    };

    // Conversations client is available, but might not be ready.
    // Meanwhile, conversations listeners can be added and conversations loaded.
    useEffect(() => {
        (async () => {
            if (!conversationsClient) return;
            console.log('setting client convo listeners');
            conversationsClient.on('conversationUpdated', handleConversationUpdated);
            conversationsClient.on('conversationJoined', handleConversationJoin);
            conversationsClient.on('conversationLeft', handleConversationLeft);

            const { sortedByNewest } = await fetchFirstPageConversations();
            setConversations(sortedByNewest);
            console.log({ convos: sortedByNewest });

            setIsFirstLoad(false);
        })();
        return () => {
            if (!conversationsClient) return;
            console.log('remove client convo listeners');
            conversationsClient.off('conversationUpdated', handleConversationUpdated);
            conversationsClient.off('conversationJoined', handleConversationJoin);
            conversationsClient.off('conversationLeft', handleConversationLeft);
            setIsReady(false);
        };
        // The listeners should only be added once. This wants to add the handlers.
        // Handlers get around not having access to updated state for other variables
        // by using conversationsClient directly and useRef.
        // I would like to find a better way to do this, but not sure what that would be.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversationsClient]);

    return (
        <ConversationsContext.Provider
            value={{
                isReady,
                conversations,
                currentConversation,
                fetchFirstPageConversations,
                fetchPaginatedConversations,
                hasMoreConversations,
                selectConversation,
                sendMessageToConversation,
            }}
        >
            {children}
        </ConversationsContext.Provider>
    );
};

ConversationsProvider.propTypes = {
    children: PropTypes.oneOfType([PropTypes.element, PropTypes.array]),
};

export default ConversationsProvider;
